import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { getPoi, searchPois } from '../../api/resources';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { PoiDetailPanel } from '../../components/detail/PoiDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { POI_TYPE_VALUES } from '../../types/domain';
import { ResourceBrowsePage } from './ResourceBrowsePage';

export function PoisPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const detailPoiId = searchParams.get('id');

    const extraListParams = useMemo(
        () => (typeFilter ? { poiType: typeFilter } : undefined),
        [typeFilter],
    );

    return (
        <>
            <ResourceBrowsePage
                title={t('features.pois.title')}
                description={t('features.pois.description')}
                queryKey="pois-search"
                fetchList={searchPois}
                fetchOne={getPoi}
                searchMode="search"
                extraListParams={extraListParams}
                filters={
                    <Select
                        label={t('fields.type')}
                        placeholder={t('common.all')}
                        clearable
                        data={POI_TYPE_VALUES.map((poiType) => ({
                            value: poiType,
                            label: t(`poiTypes.${poiType}`),
                        }))}
                        value={typeFilter}
                        onChange={setTypeFilter}
                    />
                }
                columns={[
                    {
                        key: 'name',
                        header: t('fields.name'),
                        render: (r) => (
                            <EntityRef
                                title={r.name ?? t('entities.poi')}
                                id={r.id}
                                href={`/pois?id=${r.id}`}
                                hideId
                            />
                        ),
                    },
                    {
                        key: 'address',
                        header: t('fields.address'),
                        render: (r) => {
                            const parts = [r.claimedAddress, r.claimedCity, r.claimedState].filter(
                                Boolean,
                            );
                            return parts.length > 0 ? parts.join(', ') : t('common.empty');
                        },
                    },
                    {
                        key: 'type',
                        header: t('fields.type'),
                        render: (r) =>
                            r.poiType ? (
                                <Badge variant="light" size="sm">
                                    {t(`poiTypes.${r.poiType}`, { defaultValue: r.poiType })}
                                </Badge>
                            ) : (
                                t('common.empty')
                            ),
                    },
                ]}
            />
            <DetailDrawer
                opened={Boolean(detailPoiId)}
                onClose={() => {
                    searchParams.delete('id');
                    setSearchParams(searchParams);
                }}
                title={t('detail.poi')}
                subtitle={detailPoiId ?? undefined}
            >
                {detailPoiId ? <PoiDetailPanel poiId={detailPoiId} /> : null}
            </DetailDrawer>
        </>
    );
}
